import { SafeHydrate } from "@/SafeHydrate";
import { Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
export default function MangomintBookingWidget() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Set a timeout to handle cases where iframe takes too long to load
    const timeoutId = setTimeout(() => {
      if (!isLoaded && !hasError) {
        setIsLoading(false);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [isLoaded, hasError]);

  // Set up message handler for iframe postMessage communication
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle messages from the booking widget iframe
      // Respond to postMessage calls to prevent timeout errors
      if (event.origin === "https://booking.mangomint.com") {
        try {
          if (event.data && typeof event.data === "object") {
            // Acknowledge the message to prevent postMessage timeout
            event.source?.postMessage({
              received: true
            }, event.origin);
          }
        } catch (error) {
          // Silently handle cross-origin errors
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);
  const handleIframeLoad = () => {
    setIsLoaded(true);
    setIsLoading(false);
    setHasError(false);
  };
  const handleIframeError = () => {
    setHasError(true);
    setIsLoading(false);
  };
  return <section id="mangomint-booking-widget" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-primary/5 rounded-full mb-4">
            <SafeHydrate><Calendar className="w-6 h-6 text-primary" /></SafeHydrate>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-default mb-4 text-foreground">
            Book Your Appointment
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-sans">
            Schedule your visit easily using our secure online booking system. Choose your preferred service, provider, and time below.
          </p>
        </div>

        <Card className="overflow-hidden border-border shadow-lg">
          <CardContent className="p-0">
            <div className="w-full min-h-[700px] bg-muted/10 relative flex items-center justify-center">
              {isLoading || hasError ? <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground text-center p-6 z-20">
                  <SafeHydrate><Calendar className="w-12 h-12 mb-4 opacity-20" /></SafeHydrate>
                  {hasError ? <>
                      <p className="font-medium font-default text-lg mb-1">Booking System Unavailable</p>
                      <p className="text-sm opacity-70 font-sans max-w-md">
                        We're having trouble loading the booking system. Please try again later or contact us for assistance.
                      </p>
                    </> : <>
                      <p className="font-medium font-default text-lg mb-1">Loading Booking System</p>
                      <p className="text-sm opacity-70 font-sans max-w-md">
                        Please wait while we load your booking calendar...
                      </p>
                    </>}
                </div> : null}

              <iframe src="https://booking.mangomint.com/811506" title="Mangomint Booking Widget" className={`w-full h-[700px] border-0 relative bg-transparent transition-opacity duration-300 ${isLoaded ? "opacity-100 z-10" : "opacity-0 z-0"}`} aria-label="Online booking calendar" onLoad={handleIframeLoad} onError={handleIframeError} sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation" allow="geolocation; microphone; camera" />
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}